import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { PlaintextPair } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import type { Column } from '../components/DataTable'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Sicurezza → Cifratura → Traffico in chiaro: the pairs of machines that talk
 * without encryption, with the evidence — HTTP seen without TLS by the
 * agent's eBPF or OpenTelemetry, a process connected to the port of a
 * clear-text protocol, network flows on such a port. The counters are views:
 * the pairs, the servers answering in clear text, the machines calling, and
 * the protocols filter. See plaintext_traffic_service.
 */
const PROVE: Record<string, { label: () => string; tono: string }> = {
  runtime: { label: () => t('visto dal runtime'), tono: 'error' },
  connection: { label: () => t('connessione per processo'), tono: 'warn' },
  flow: { label: () => t('flussi di rete'), tono: 'muted' },
}

type Nodo = PlaintextPair['client']
type Vista = 'coppie' | 'server' | 'client'

interface Gruppo {
  nodo: Nodo
  servizi: string[]
  altri: number
  prove: string[]
  chiamate: number
}

function nodo(n: Nodo) {
  return n.ci_id
    ? <><Link to={`/cis/${n.ci_id}`}>{n.name}</Link>{n.address && n.address !== n.name && <small className="text-muted"> · {n.address}</small>}</>
    : <span className="mono">{n.name}</span>
}

function prove(evidence: string[]) {
  return evidence.map((e) => (
    <span key={e} className={`badge badge--${PROVE[e]?.tono ?? 'muted'}`} style={{ marginRight: 4 }}>{PROVE[e]?.label() ?? e}</span>
  ))
}

/** The pairs grouped by one side: per server (who answers) or per client. */
function raggruppa(coppie: PlaintextPair[], lato: 'server' | 'client'): Gruppo[] {
  const per = new Map<string, Gruppo & { altriSet: Set<string>; serviziSet: Set<string>; proveSet: Set<string> }>()
  for (const c of coppie) {
    const qui = lato === 'server' ? c.server : c.client
    const la = lato === 'server' ? c.client : c.server
    const g = per.get(qui.key) ?? { nodo: qui, servizi: [], altri: 0, prove: [], chiamate: 0,
      altriSet: new Set<string>(), serviziSet: new Set<string>(), proveSet: new Set<string>() }
    g.altriSet.add(la.key)
    g.serviziSet.add(`${c.protocol} :${c.port}`)
    c.evidence.forEach((e) => g.proveSet.add(e))
    g.chiamate += c.count
    per.set(qui.key, g)
  }
  return [...per.values()].map((g) => ({
    nodo: g.nodo, servizi: [...g.serviziSet].sort(), altri: g.altriSet.size, prove: [...g.proveSet], chiamate: g.chiamate,
  }))
}

function Contatore({ n, etichetta, attivo, tono, onClick }: {
  n: number; etichetta: string; attivo: boolean; tono?: string; onClick: () => void
}) {
  return (
    <button type="button" className={`stat${tono ? ` stat--${tono}` : ''}`} aria-pressed={attivo} onClick={onClick}
      style={attivo ? { borderColor: 'var(--accent)', boxShadow: 'var(--shadow), 0 0 0 1px var(--accent)' } : undefined}>
      <div className="stat__value">{n.toLocaleString(localeCorrente())}</div>
      <div className="stat__label">{etichetta}</div>
    </button>
  )
}

export function TrafficoInChiaro() {
  const [ore, setOre] = useState(24)
  const [vista, setVista] = useState<Vista>('coppie')
  const [protocollo, setProtocollo] = useState<string | null>(null)
  // A server or a client chosen from the grouped views: the pairs of that one.
  const [scelto, setScelto] = useState<{ lato: 'server' | 'client'; nodo: Nodo } | null>(null)
  const dati = useAsync(() => api.plaintextTraffic(ore), [ore])

  const coppie = (dati.data?.items ?? []).filter((c) =>
    (!protocollo || c.protocol === protocollo)
    && (!scelto || (scelto.lato === 'server' ? c.server.key : c.client.key) === scelto.nodo.key))

  const colonne: Column<PlaintextPair>[] = [
    { key: 'client', header: t('Chi chiama'), value: (r) => r.client.name, render: (r) => (
      <>{nodo(r.client)}{r.processes.length > 0 && <><br /><small className="text-muted">{r.processes.join(', ')}</small></>}</>
    ) },
    { key: 'server', header: t('Chi risponde'), value: (r) => r.server.name, render: (r) => nodo(r.server) },
    { key: 'protocol', header: t('Protocollo'), value: (r) => `${r.protocol} ${r.port}`, render: (r) => (
      <><strong>{r.protocol}</strong> <span className="mono text-muted">{r.port}</span></>
    ) },
    { key: 'evidence', header: t('Come lo sappiamo'), value: (r) => r.evidence.join(' '), render: (r) => <>{prove(r.evidence)}</> },
    { key: 'count', header: t('Chiamate o flussi'), value: (r) => r.count, filter: false },
    { key: 'map', header: '', filter: false, render: (r) => r.client.ci_id
      ? <Link to={`/software-flow?ci=${r.client.ci_id}`}>{t('Mappa Software')}</Link> : null },
  ]
  const colonneGruppo = (lato: 'server' | 'client'): Column<Gruppo>[] => [
    { key: 'nodo', header: lato === 'server' ? t('Server') : t('Macchina che chiama'), value: (g) => g.nodo.name,
      render: (g) => nodo(g.nodo) },
    { key: 'servizi', header: lato === 'server' ? t('Servizi in chiaro') : t('Servizi chiamati in chiaro'),
      value: (g) => g.servizi.join(' '), render: (g) => <span className="mono">{g.servizi.join(', ')}</span> },
    { key: 'altri', header: lato === 'server' ? t('Client') : t('Server'), value: (g) => g.altri, filter: false },
    { key: 'prove', header: t('Come lo sappiamo'), value: (g) => g.prove.join(' '), render: (g) => <>{prove(g.prove)}</> },
    { key: 'vedi', header: '', filter: false, sort: false, render: (g) => (
      <button type="button" className="link" onClick={() => { setScelto({ lato, nodo: g.nodo }); setVista('coppie') }}>
        {t('Vedi le coppie')}
      </button>
    ) },
  ]

  if (dati.loading && !dati.data) return <Spinner />
  return (
    <>
      <ErrorBanner error={dati.error} />
      <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <p className="field__hint" style={{ margin: 0, flex: 1 }}>
          {t('Individua le coppie di asset che comunicano in chiaro, esponendo potenzialmente dati e credenziali a chi può osservare il traffico. Le evidenze sono ordinate per affidabilità: traffico HTTP non cifrato osservato a livello applicativo, processi associati a protocolli intrinsecamente non cifrati e flussi di rete sulle relative porte. I protocolli che possono usare TLS sulla stessa porta vengono segnalati solo quando il traffico in chiaro è stato effettivamente osservato. Le comunicazioni interne allo stesso host sono escluse.')}
        </p>
        <label>{t('Periodo')}{' '}
          <select value={ore} onChange={(e) => setOre(Number(e.target.value))}>
            <option value={24}>{t('ultime 24 ore')}</option>
            <option value={168}>{t('ultimi 7 giorni')}</option>
            <option value={720}>{t('ultimi 30 giorni')}</option>
          </select>
        </label>
      </div>
      {dati.data && (
        dati.data.items.length === 0 ? (
          <Card><EmptyState title={t('Nessun traffico in chiaro trovato')}
            hint={t('Le prove arrivano dalle connessioni lette dall’agente CENSO e dall’inventario avanzato, dall’osservazione applicativa (eBPF) e dai flussi di rete.')} /></Card>
        ) : (
          <>
            <div className="stats">
              <Contatore n={dati.data.items.length} etichetta={t('Coppie in chiaro')} tono="warn" attivo={vista === 'coppie'}
                onClick={() => { setVista('coppie'); setScelto(null) }} />
              <Contatore n={dati.data.servers} etichetta={t('Server che rispondono in chiaro')} attivo={vista === 'server'}
                onClick={() => { setVista('server'); setScelto(null) }} />
              <Contatore n={dati.data.clients} etichetta={t('Macchine che chiamano in chiaro')} attivo={vista === 'client'}
                onClick={() => { setVista('client'); setScelto(null) }} />
            </div>
            <div className="toolbar" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              <small className="text-muted">{t('Per protocollo')}:</small>
              <button type="button" className={protocollo ? 'secondary' : ''} onClick={() => setProtocollo(null)}>{t('tutti')}</button>
              {Object.entries(dati.data.by_protocol).sort((a, b) => b[1] - a[1]).map(([p, n]) => (
                <button key={p} type="button" className={protocollo === p ? '' : 'secondary'}
                  onClick={() => setProtocollo(protocollo === p ? null : p)}>{p} ({n})</button>
              ))}
              {scelto && (
                <button type="button" className="secondary" onClick={() => setScelto(null)}>
                  {scelto.lato === 'server' ? t('Server') : t('Macchina che chiama')}: {scelto.nodo.name} ×
                </button>
              )}
            </div>
            <Card>
              {vista === 'coppie' ? (
                <DataTable rows={coppie} rowKey={(r) => `${r.client.key}>${r.server.key}:${r.port}`} columns={colonne}
                  initialSort={{ key: 'count', dir: 'desc' }} conteggio={{ nome: t('coppie') }} paginazione={{ predefinita: 50 }} />
              ) : (
                <DataTable rows={raggruppa(coppie, vista)} rowKey={(g) => g.nodo.key} columns={colonneGruppo(vista)}
                  initialSort={{ key: 'altri', dir: 'desc' }} paginazione={{ predefinita: 50 }} />
              )}
            </Card>
          </>
        )
      )}
    </>
  )
}
